<template>
  <form @submit.prevent="$emit('submit')">
    <div class="row mb-40">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.date") }}</label>
          <input
            type="date"
            :value="form.date"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white p-3"
            :readonly="lockAutoFields"
            @input="$emit('update-field', 'date', ($event.target as HTMLInputElement).value)"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.reference_no") }}</label>
          <input
            type="text"
            :value="form.reference_no"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white p-3"
            :placeholder="$t('quotation_page.reference_no')"
            :readonly="lockAutoFields"
            @input="$emit('update-field', 'reference_no', ($event.target as HTMLInputElement).value)"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.customer") }}</label>
          <select
            :value="form.customer_id ?? ''"
            class="form-select bg-white border-0 rounded-1 fs-14 text-optional h-55"
            @change="$emit('update-field', 'customer_id', toNullableNumber($event))"
          >
            <option value="">{{ $t("quotation_page.select_customer") }}</option>
            <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.warehouse") }}</label>
          <select
            :value="form.warehouse_id ?? ''"
            class="form-select bg-white border-0 rounded-1 fs-14 text-optional h-55"
            :disabled="warehouseLockedToProfile"
            @change="$emit('update-field', 'warehouse_id', toNullableNumber($event))"
          >
            <option value="">{{ $t("quotation_page.select_warehouse") }}</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.status") }}</label>
          <select
            :value="form.status"
            class="form-select bg-white border-0 rounded-1 fs-14 text-optional h-55"
            @change="$emit('update-field', 'status', ($event.target as HTMLSelectElement).value)"
          >
            <option value="pending">{{ $t("quotation_page.status_pending") }}</option>
            <option value="sent">{{ $t("quotation_page.status_sent") }}</option>
            <option value="completed">{{ $t("quotation_page.status_completed") }}</option>
          </select>
        </div>
      </div>
      <div class="col-12">
        <div class="form-group">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.choose_product") }}</label>
          <div class="search-area style-two position-relative w-100">
            <input
              type="text"
              :value="searchQuery"
              @input="$emit('update-search', ($event.target as HTMLInputElement).value)"
              :placeholder="$t('quotation_page.scan_search_product')"
              class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            />
            <button
              type="button"
              class="bg-transparent border-0 position-absolute top-0 end-0 h-100 pt-0 py-0 px-2"
            >
              <img src="../../../assets/img/icons/search.svg" alt="Image" />
            </button>
            <div v-if="showResults && searchResults.length > 0" class="search-results-dropdown position-absolute w-100 bg-white shadow rounded-1 mt-1 z-3">
              <button
                v-for="p in searchResults"
                :key="p.id"
                type="button"
                :disabled="!p.canSelect"
                @click="$emit('add-product', p)"
                class="result-item w-100 text-start border-0 bg-transparent"
                :class="{ 'result-item-disabled': !p.canSelect }"
              >
                <div class="d-flex justify-content-between gap-3">
                  <div>
                    <div class="fw-semibold text-title">{{ p.name }}</div>
                    <div class="text-muted small">{{ p.code }}</div>
                  </div>
                  <div class="text-end">
                    <div class="fw-semibold">${{ Number(p.price || 0).toFixed(2) }}</div>
                    <div class="small text-muted">
                      <span v-if="Number(p.type ?? 1) === 1">
                        {{ $t("quotation_page.stock") }}: {{ p.stock }}
                        <span v-if="!p.canSelect" class="text-danger ms-1">({{ $t("quotation_page.out_of_stock") }})</span>
                      </span>
                      <span v-else>{{ $t("quotation_page.service_item") }}</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card border-0 shadow-none rounded-1 mb-30">
      <div class="card-body p-xl-40">
        <h6 class="fs-18 mb-35 text-title fw-semibold">{{ $t("quotation_page.selected_products_for_quotation") }}</h6>
        <div class="table-responsive">
          <table class="table text-nowrap align-middle mb-0">
            <thead>
              <tr>
                <th class="text-title fw-normal fs-14 pt-0 ps-0 ls-1">{{ $t("quotation_page.product") }}</th>
                <th class="text-title fw-normal fs-14 pt-0 ls-1">{{ $t("quotation_page.code") }}</th>
                <th class="text-title fw-normal fs-14 pt-0 ls-1">{{ $t("quotation_page.unit_price") }}</th>
                <th class="text-title fw-normal fs-14 pt-0 ls-1">{{ $t("quotation_page.quantity") }}</th>
                <th class="text-title fw-normal fs-14 pt-0 ls-1">{{ $t("quotation_page.subtotal") }}</th>
                <th class="text-title fw-normal fs-14 pt-0 ls-1 text-end pe-0">{{ $t("quotation_page.delete") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in form.items" :key="`${item.product_id}-${index}`">
                <td class="fs-14 fw-normal text-paragraph ps-0">{{ item.product_name }}</td>
                <td class="fs-14 fw-normal text-paragraph">{{ item.product_code }}</td>
                <td class="fs-14 fw-normal text-paragraph">${{ Number(item.unit_price || 0).toFixed(2) }}</td>
                <td class="fs-14 fw-normal text-paragraph">
                  <div class="d-flex align-items-center">
                    <button type="button" @click="$emit('update-qty', index, -1)" class="btn btn-sm btn-outline-secondary">-</button>
                    <input
                      type="number"
                      :value="item.quantity"
                      class="form-control form-control-sm mx-2 text-center"
                      style="width: 60px"
                      @input="$emit('set-qty', index, Number(($event.target as HTMLInputElement).value))"
                    />
                    <button type="button" @click="$emit('update-qty', index, 1)" class="btn btn-sm btn-outline-secondary">+</button>
                  </div>
                </td>
                <td class="fs-14 fw-normal text-paragraph">
                  ${{ (Number(item.unit_price || 0) * Number(item.quantity || 0)).toFixed(2) }}
                </td>
                <td class="text-end pe-0">
                  <a href="javascript:void(0)" class="delete-btn cursor-pointer" @click="$emit('remove-item', index)">
                    <img src="../../../assets/img/icons/close.svg" alt="Image" />
                  </a>
                </td>
              </tr>
              <tr v-if="form.items.length === 0">
                <td colspan="6" class="text-center p-4 text-muted">{{ $t("quotation_page.no_products_selected") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row mb-40">
      <div class="col-lg-8">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.note") }}</label>
          <textarea
            :value="form.note"
            class="form-control"
            rows="4"
            :placeholder="$t('quotation_page.optional_notes')"
            @input="$emit('update-field', 'note', ($event.target as HTMLTextAreaElement).value)"
          ></textarea>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.order_tax") }}</label>
              <div class="input-group">
                <input
                  type="number"
                  :value="form.tax"
                  class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                  placeholder="0"
                  @input="$emit('update-field', 'tax', Number(($event.target as HTMLInputElement).value))"
                />
                <select
                  :value="form.tax_type"
                  class="form-select shadow-none fs-14 bg-light text-title totals-type-select"
                  @change="$emit('update-field', 'tax_type', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="fixed">$</option>
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.order_discount") }}</label>
              <div class="input-group">
                <input
                  type="number"
                  :value="form.discount"
                  class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                  placeholder="0"
                  @input="$emit('update-field', 'discount', Number(($event.target as HTMLInputElement).value))"
                />
                <select
                  :value="form.discount_type"
                  class="form-select shadow-none fs-14 bg-light text-title totals-type-select"
                  @change="$emit('update-field', 'discount_type', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="fixed">$</option>
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("quotation_page.shipping_cost") }}</label>
              <div class="input-group">
                <input
                  type="number"
                  :value="form.shipping"
                  class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                  placeholder="0"
                  @input="$emit('update-field', 'shipping', Number(($event.target as HTMLInputElement).value))"
                />
                <span class="input-group-text shipping-addon fs-16 fw-semibold">$</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="card border-0 rounded-1 p-4 mb-4">
          <div class="d-flex justify-content-between mb-2">
            <span class="fs-14">{{ $t("quotation_page.total_items") }}:</span>
            <span class="fs-14 fw-bold">{{ totalItems }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span class="fs-14">{{ $t("quotation_page.subtotal") }}:</span>
            <span class="fs-14 fw-semibold">${{ totalCost.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span class="fs-14">{{ $t("quotation_page.discount") }}:</span>
            <span class="fs-14 fw-semibold text-danger">-${{ calculatedDiscount.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span class="fs-14">{{ $t("quotation_page.tax") }}:</span>
            <span class="fs-14 fw-semibold">${{ calculatedTax.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span class="fs-14">{{ $t("quotation_page.shipping") }}:</span>
            <span class="fs-14 fw-semibold">${{ Number(form.shipping || 0).toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2 border-top pt-2">
            <span class="fs-18 fw-bold">{{ $t("quotation_page.grand_total") }}:</span>
            <span class="fs-18 fw-bold text-primary">${{ grandTotal.toFixed(2) }}</span>
          </div>
        </div>
        <button type="submit" class="btn btn-primary w-100 py-3 fw-bold" :disabled="isSubmitting || form.items.length === 0">
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
          {{ isEditMode ? $t("quotation_page.update_quotation") : $t("create_quotation") }}
        </button>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { Customer } from "../../../services/customer";
import type { Warehouse } from "../../../services/warehouse";
import type { QuotationItem } from "../../../services/quotation";

type ProductOption = {
  id?: number;
  code?: string;
  name?: string;
  price?: number;
  type?: number;
  stock: number;
  canSelect: boolean;
};

type QuotationFormState = {
  date: string;
  reference_no: string;
  customer_id: number | null;
  warehouse_id: number | null;
  status: string;
  note: string;
  tax: number;
  tax_type: "fixed" | "percentage";
  discount: number;
  discount_type: "fixed" | "percentage";
  shipping: number;
  items: QuotationItem[];
};

export default defineComponent({
  name: "QuotationForm",
  props: {
    form: {
      type: Object as PropType<QuotationFormState>,
      required: true,
    },
    customers: {
      type: Array as PropType<Customer[]>,
      default: () => [],
    },
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      default: () => [],
    },
    searchQuery: {
      type: String,
      required: true,
    },
    searchResults: {
      type: Array as PropType<ProductOption[]>,
      default: () => [],
    },
    showResults: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    totalCost: {
      type: Number,
      required: true,
    },
    calculatedDiscount: {
      type: Number,
      required: true,
    },
    calculatedTax: {
      type: Number,
      required: true,
    },
    grandTotal: {
      type: Number,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    warehouseLockedToProfile: {
      type: Boolean,
      default: false,
    },
    lockAutoFields: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update-field", "update-search", "add-product", "update-qty", "set-qty", "remove-item", "submit"],
  setup() {
    const toNullableNumber = (event: Event) => {
      const value = (event.target as HTMLSelectElement).value;
      return value === "" ? null : Number(value);
    };

    return {
      toNullableNumber,
    };
  },
});
</script>

<style scoped>
.search-results-dropdown {
  max-height: 280px;
  overflow-y: auto;
}

.result-item {
  padding: 12px 16px;
  border-bottom: 1px solid #eef2f6;
}

.result-item:hover {
  background: #f8fafc;
}

.result-item-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.result-item-disabled:hover {
  background: transparent;
}

.totals-type-select {
  max-width: 72px;
}

.shipping-addon {
  min-width: 56px;
  justify-content: center;
  background: #f4f6fb;
  color: #5b6575;
  border-color: #dee2e6;
}
</style>
